"use client";

import type { ReactNode } from "react";
import { useState, useSyncExternalStore } from "react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Drawer as DrawerPrimitive } from "vaul";

import { clinicUrl, type Clinic } from "@/lib/clinics";

const SITE = {
  uk: { site: "UK", country: "the UK" },
  ie: { site: "Ireland", country: "Ireland" },
} as const;

const DESKTOP_QUERY = "(min-width: 768px)";

function subscribeDesktop(onChange: () => void) {
  const mq = window.matchMedia(DESKTOP_QUERY);
  mq.addEventListener("change", onChange);
  return () => mq.removeEventListener("change", onChange);
}

function useIsDesktop() {
  return useSyncExternalStore(
    subscribeDesktop,
    () => window.matchMedia(DESKTOP_QUERY).matches,
    () => false,
  );
}

type Props = {
  clinic: Clinic;
  className?: string;
  children: ReactNode;
  [attr: `data-${string}`]: string | boolean | undefined;
};

// A clinic link that warns before sending the user across regions, since the
// destination switches them to our other country's site.
export function ClinicLink({ clinic, className, children, ...dataAttrs }: Props) {
  const pathname = usePathname();
  const router = useRouter();
  const isDesktop = useIsDesktop();
  const [open, setOpen] = useState(false);

  const target = clinicUrl(clinic);
  const clinicRegion = clinic.fullSlug.startsWith("uk/") ? "uk" : "ie";
  const pageRegion =
    pathname === "/uk" || pathname.startsWith("/uk/") ? "uk" : "ie";
  const crossRegion = clinicRegion !== pageRegion;
  const { site, country } = SITE[clinicRegion];

  const link = (
    <Link
      href={target}
      className={className}
      onClick={(e) => {
        if (crossRegion) {
          e.preventDefault();
          setOpen(true);
        }
      }}
      {...dataAttrs}
    >
      {children}
    </Link>
  );

  if (!crossRegion) return link;

  const heading = `Visit our ${site} site?`;
  const message = `${clinic.name} is one of our clinics in ${country}. You’ll be taken to our ${site} site to view it.`;
  const go = () => {
    setOpen(false);
    router.push(target);
  };

  return (
    <>
      {link}
      {isDesktop ? (
        <DialogPrimitive.Root open={open} onOpenChange={setOpen}>
          <DialogPrimitive.Portal>
            <DialogPrimitive.Overlay className="fixed inset-0 z-60 bg-black/60 backdrop-blur-[2px]" />
            <DialogPrimitive.Content
              className="fixed left-1/2 top-1/2 z-60 w-[calc(100%-2.5rem)] max-w-[560px] -translate-x-1/2 -translate-y-1/2 rounded-3xl bg-white p-8 shadow-[0_40px_80px_-24px_rgba(0,0,0,0.35)] outline-none"
              style={{ animation: "clinic-region-dialog-in 0.2s ease-out" }}
            >
              <DialogPrimitive.Title className="text-h6 text-purple-05 font-bold">
                {heading}
              </DialogPrimitive.Title>
              <DialogPrimitive.Description className="text-body mt-2 text-black/65">
                {message}
              </DialogPrimitive.Description>
              <div className="mt-7 flex justify-end gap-3">
                <DialogPrimitive.Close className="text-button rounded-[8px] border border-black/10 px-5 py-2.5 font-bold whitespace-nowrap text-black/70 transition-colors hover:bg-black/5">
                  Stay here
                </DialogPrimitive.Close>
                <button
                  type="button"
                  onClick={go}
                  className="bg-purple-03 hover:bg-purple-04 text-button rounded-[8px] px-5 py-2.5 font-bold whitespace-nowrap text-white transition-colors"
                >
                  Continue to the {site} site
                </button>
              </div>
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
          <style jsx global>{`
            @keyframes clinic-region-dialog-in {
              from {
                opacity: 0;
                transform: scale(0.96);
              }
              to {
                opacity: 1;
                transform: scale(1);
              }
            }
          `}</style>
        </DialogPrimitive.Root>
      ) : (
        <DrawerPrimitive.Root open={open} onOpenChange={setOpen}>
          <DrawerPrimitive.Portal>
            <DrawerPrimitive.Overlay className="fixed inset-0 z-60 bg-black/60 backdrop-blur-[2px]" />
            <DrawerPrimitive.Content className="fixed inset-x-0 bottom-0 z-60 mx-auto flex max-w-md flex-col rounded-t-[24px] bg-white text-black shadow-[0_-20px_40px_-12px_rgba(0,0,0,0.25)] outline-none">
              <div className="mx-auto mt-3 h-1.5 w-10 shrink-0 rounded-full bg-black/15" />
              <div className="p-6 pb-[calc(env(safe-area-inset-bottom)+1.5rem)]">
                <DrawerPrimitive.Title className="text-h6 text-purple-05 font-bold">
                  {heading}
                </DrawerPrimitive.Title>
                <DrawerPrimitive.Description className="text-body mt-2 text-black/65">
                  {message}
                </DrawerPrimitive.Description>
                <div className="mt-6 flex flex-col gap-2.5">
                  <button
                    type="button"
                    onClick={go}
                    className="bg-purple-03 hover:bg-purple-04 text-button rounded-[8px] px-4 py-3 font-bold text-white transition-colors"
                  >
                    Continue to the {site} site
                  </button>
                  <DrawerPrimitive.Close className="text-button rounded-[8px] border border-black/10 px-4 py-3 font-bold text-black/70 transition-colors hover:bg-black/5">
                    Stay here
                  </DrawerPrimitive.Close>
                </div>
              </div>
            </DrawerPrimitive.Content>
          </DrawerPrimitive.Portal>
        </DrawerPrimitive.Root>
      )}
    </>
  );
}
